import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useChainData } from 'modules/common';
import { DashboardRoutesConfig } from 'modules/dashboard';
import { useTranslation } from 'modules/i18n';
import { Layout } from 'modules/layout';
import { useGetAccountAddressQuery } from 'modules/store';

import { useLazyCreateCompatibleTaskQuery } from '../actions/createCompatibleTask';
import { useCreateTaskMutation } from '../actions/createTask';
import { SubmitState, TaskForms } from '../components/TaskForms';
import { TaskPage } from '../components/TaskPage';
import translation from './createTask.translation';

export function CreateTask(): JSX.Element {
  const { t, keys } = useTranslation(translation);
  const navigate = useNavigate();
  const { data: accountAddress = '' } = useGetAccountAddressQuery();
  const chainData = useChainData();
  const [createTask, { isLoading }] = useCreateTaskMutation();
  const [createCompatibleTask, { isLoading: isCompatibleLoading }] =
    useLazyCreateCompatibleTaskQuery();

  const onSubmit = async ({
    taskName,
    taskContractAddress,
    gasLimit,
    abi,
    abiItem,
    params,
    cronExpression,
    isCompatible,
    isCustomAbi,
    startDate,
  }: SubmitState): Promise<void> => {
    const result = await (() => {
      const chainId = chainData?.chainId;
      if (!chainId) {
        throw new Error('Chain Id is not defined');
      }

      if (isCompatible) {
        return createCompatibleTask({
          accountAddress,
          chainId,
          taskName,
          taskContractAddress,
          gasLimit,
        });
      }

      if (!abiItem) {
        throw new Error('ABI is not defined');
      }

      return createTask({
        abi,
        abiItem,
        accountAddress,
        cronExpression,
        isCustomAbi,
        taskName,
        taskContractAddress,
        params,
        gasLimit,
        chainId: chainData?.chainId || null,
        startDate: startDate ?? new Date(),
      });
    })();

    // eslint-disable-next-line no-prototype-builtins
    if (result.hasOwnProperty('error')) {
      return;
    }

    navigate(DashboardRoutesConfig.dashboard.generatePath());
  };

  return (
    <Layout>
      <Box
        sx={theme => ({
          padding: theme.spacing(10, 0),

          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(8, 0),
          },
        })}
      >
        <TaskPage title={t(keys.title)}>
          <TaskForms
            isLoading={isLoading || isCompatibleLoading}
            onSubmit={onSubmit}
          />
        </TaskPage>
      </Box>
    </Layout>
  );
}
